// apps/web/src/pages/vendas/importacao/passo-previa.tsx — Fase 12, Plano 25 (PV-03, D-30, D-31).
// Passo 3: resumo, aviso de reimportação, linhas agrupadas por nº do pedido (virtualizadas acima de 200),
// filtro "só com problema" e confirmação segura (erros exigem a caixa "Ignorar as N linhas com erro").
import { useMemo, useRef } from 'react';
import { CheckCircle2, Loader2, PackagePlus, XCircle } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { LinhaPreviaImportacao, PreviaImportacao } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { type PvJaImportado } from '../../../lib/vendas/erro-api';
import { LIMIAR_VIRTUALIZACAO } from '../../../lib/kanban/volume';
import { formatarData, formatarDataLocal, formatarMoeda, formatarQuantidade } from '../../../lib/vendas/formatacao';
import {
  MSG_SEM_LINHAS,
  achatarGrupos,
  filtrarLinhasComProblema,
  podeConfirmar,
  rotuloCriarPvs,
  rotuloLinhaPrevia,
  rotuloResumoPrevia,
  type LinhaAchatada,
} from '../../../lib/vendas/importacao';

const ALTURA_LINHA_ESTIMADA = 44;

interface PassoPreviaProps {
  readonly previa: PreviaImportacao;
  readonly ignorarErros: boolean;
  readonly onIgnorarErros: (valor: boolean) => void;
  readonly apenasProblemas: boolean;
  readonly onApenasProblemas: (valor: boolean) => void;
  readonly erro: string | null;
  /** PVs já criados pela mesma planilha (409): links para abrir cada um. */
  readonly pvsJaCriados: readonly PvJaImportado[];
  /** Resultado incerto (rede/5xx): convite a conferir a lista de pedidos antes de tentar de novo. */
  readonly conferirPedidos: boolean;
  readonly ocupado: boolean;
  readonly onVoltar: () => void;
  readonly onConfirmar: () => void;
}

function StatusLinha({ linha }: { readonly linha: LinhaPreviaImportacao }) {
  const texto = rotuloLinhaPrevia(linha);
  if (linha.status === 'ERRO') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
        <XCircle className="size-3.5 shrink-0" aria-hidden="true" />
        {texto}
      </span>
    );
  }
  if (linha.status === 'ITEM_NOVO') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        <PackagePlus className="size-3.5 shrink-0" aria-hidden="true" />
        {texto}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />
      {texto}
    </span>
  );
}

function LinhaTabela({ item }: { readonly item: LinhaAchatada }) {
  if (item.tipo === 'grupo') {
    return (
      <tr className="border-t bg-slate-50">
        <th scope="colgroup" colSpan={7} className="px-3 py-2 text-left text-sm font-semibold">
          {item.titulo}
        </th>
      </tr>
    );
  }
  const { linha } = item;
  return (
    <tr className="border-t align-top">
      <td className="px-3 py-2 tabular-nums">{linha.linha}</td>
      <td className="px-3 py-2">{linha.codigoItemCliente ?? '—'}</td>
      <td className="max-w-[16rem] truncate px-3 py-2">{linha.descricao ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(linha.quantidade, linha.unidade ?? '')}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(linha.preco)}</td>
      <td className="px-3 py-2 tabular-nums">{formatarData(linha.prazo)}</td>
      <td className="px-3 py-2">
        <StatusLinha linha={linha} />
      </td>
    </tr>
  );
}

function Cabecalho() {
  return (
    <thead className="bg-slate-100 text-left">
      <tr>
        {['Linha', 'Código do cliente', 'Descrição', 'Quantidade', 'Preço', 'Prazo', 'Status'].map((titulo, indice) => (
          <th key={titulo} scope="col" className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${indice === 3 || indice === 4 ? 'text-right' : ''}`}>
            {titulo}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function PassoPrevia(props: PassoPreviaProps) {
  const { previa, ignorarErros, apenasProblemas, erro, ocupado } = props;
  const { resumo } = previa;
  const itensValidos = resumo.itens - resumo.comErro;
  const vazio = resumo.itens === 0 || previa.grupos.length === 0;

  const grupos = useMemo(() => (apenasProblemas ? filtrarLinhasComProblema(previa.grupos) : previa.grupos), [apenasProblemas, previa.grupos]);
  const linhas = useMemo(() => achatarGrupos(grupos), [grupos]);
  const virtualizar = linhas.length > LIMIAR_VIRTUALIZACAO;

  const rolagemRef = useRef<HTMLDivElement>(null);
  const virtualizador = useVirtualizer({
    count: virtualizar ? linhas.length : 0,
    getScrollElement: () => rolagemRef.current,
    estimateSize: () => ALTURA_LINHA_ESTIMADA,
    overscan: 10,
  });
  const virtuais = virtualizador.getVirtualItems();
  const antes = virtuais.length > 0 ? (virtuais[0]?.start ?? 0) : 0;
  const depois = virtuais.length > 0 ? virtualizador.getTotalSize() - (virtuais[virtuais.length - 1]?.end ?? 0) : 0;

  return (
    <div className="flex flex-col gap-4">
      <p role="status" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold">
        {rotuloResumoPrevia(resumo)}
      </p>

      {previa.jaImportado.length > 0 && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <p className="font-semibold">Este arquivo já foi importado para este cliente. Confirmar de novo cria PVs duplicados.</p>
          <ul className="mt-1 list-disc pl-5">
            {previa.jaImportado.map((pv) => (
              <li key={pv.pvId}>
                <a className="underline" href={`/vendas/pedidos/detalhe?id=${encodeURIComponent(pv.pvId)}`}>{`PV ${pv.numero}`}</a>
                {` · criado em ${formatarDataLocal(pv.createdAt)}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {vazio ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{MSG_SEM_LINHAS}</p>
      ) : (
        <>
          <label htmlFor="previa-so-problemas" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox id="previa-so-problemas" checked={apenasProblemas} onCheckedChange={(marcada) => props.onApenasProblemas(marcada === true)} />
            Mostrar só linhas com problema
          </label>

          {linhas.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma linha com problema.</p>
          ) : virtualizar ? (
            <div ref={rolagemRef} className="max-h-[60vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <caption className="sr-only">Prévia da importação</caption>
                <Cabecalho />
                <tbody aria-hidden="true">
                  <tr style={{ height: antes }} />
                </tbody>
                {virtuais.map((virtual) => {
                  const item = linhas[virtual.index];
                  if (!item) return null;
                  return (
                    <tbody key={item.chave} data-index={virtual.index} ref={virtualizador.measureElement}>
                      <LinhaTabela item={item} />
                    </tbody>
                  );
                })}
                <tbody aria-hidden="true">
                  <tr style={{ height: depois }} />
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <caption className="sr-only">Prévia da importação</caption>
                <Cabecalho />
                <tbody>
                  {linhas.map((item) => (
                    <LinhaTabela key={item.chave} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {resumo.comErro > 0 && (
        <label htmlFor="previa-ignorar-erros" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
          <Checkbox id="previa-ignorar-erros" checked={ignorarErros} disabled={ocupado} onCheckedChange={(marcada) => props.onIgnorarErros(marcada === true)} />
          {`Ignorar as ${resumo.comErro} linhas com erro`}
        </label>
      )}

      {erro && (
        <div role="alert" className="flex flex-col gap-1 text-sm text-rose-700">
          <p>{erro}</p>
          {props.pvsJaCriados.length > 0 && (
            <ul className="list-disc pl-5">
              {props.pvsJaCriados.map((pv) => (
                <li key={pv.id || pv.numero}>
                  {pv.id ? (
                    <a className="underline" href={`/vendas/pedidos/detalhe?id=${encodeURIComponent(pv.id)}`}>{`PV ${pv.numero}`}</a>
                  ) : (
                    `PV ${pv.numero}`
                  )}
                </li>
              ))}
            </ul>
          )}
          {props.conferirPedidos && (
            <a className="underline" href="/vendas/pedidos">
              Abrir a lista de pedidos
            </a>
          )}
        </div>
      )}

      <div className="sticky bottom-0 flex items-center gap-2 border-t bg-background py-3">
        <Button type="button" variant="outline" disabled={ocupado} onClick={props.onVoltar}>
          Voltar
        </Button>
        <Button type="button" disabled={ocupado || !podeConfirmar({ comErro: resumo.comErro, ignorarErros, itensValidos })} onClick={props.onConfirmar}>
          {ocupado && <Loader2 className="animate-spin" aria-hidden="true" />}
          {rotuloCriarPvs(resumo.pvs)}
        </Button>
      </div>
    </div>
  );
}
